import "../../../css/MissionControll.css";
import "leaflet/dist/leaflet.css";
import { MapContainer, Polyline, TileLayer } from "react-leaflet";
import FloatingSearch from "../../mission-control/FloatingSearch";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  locationSocketUrl,
  pathColors,
  routeService,
  STATUS_GROUPS,
  statusConst,
} from "../../../const";
import WebSocketConnection from "../../../service/WebSocketConnection";
import { useSelector } from "react-redux";
import "leaflet-rotatedmarker";
import MovingMarker from "../leaflet/MovingMarker";
import VehiclePopup from "../common/VehiclePopUp";
import useLiveLocation from "../../../hooks/useLiveLocation";
import MarkerClusterGroup from "react-leaflet-cluster";
import MoreInfo from "../../mission-control/MoreInfo/MoreInfo";
import SideNav from "../../mission-control/SideNav";
import { Settings, Sliders } from "react-feather";
import SearchFiltration from "../../mission-control/SearchFilter/SearchFiltration";
import MapSettings from "../../mission-control/MapSettings";
import { getDropdownData } from "../../../api/keyvalue";
import LoadingSpinner from "../../LoadingSpinner";
import { statusByContext } from "../../../api/statusApi";

const MissionControlMapOS = ({ jobVehicleInfoToggle }) => {
  const results = useSelector((state) => state.missionControl.results);
  const showPath = useSelector((state) => state.missionControl.showPath);

  const [vehicleLiveLocations, setVehicleLiveLocations] = useState({});
  const [vehiclePathData, setVehiclePathData] = useState({});
  const user = useSelector((state) => state.user);

  const [showVehicleJobInfo, setShowVehicleJobInfo] = useState(false);

  const [job, setJob] = useState(null);
  const [vehNo, setVehNo] = useState("N/A");
  const [vehModel, setVehModel] = useState("N/A");
  const [vehManufacturer, setVehManufacturer] = useState("N/A");

  const [openSideNav, setOpenSideNav] = useState(false);

  const [eventOptions, setEventOptions] = useState([]);
  const [jobStatusOptions, setJobStatusOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const locationEvents = await getDropdownData("location_event");
      const jobStatus = await statusByContext(STATUS_GROUPS.MISSION_CONTROL);
      setJobStatusOptions(jobStatus);
      setEventOptions(locationEvents);
      setIsLoading(false);
    })();
  }, []);

  const closeJobInfo = () => {
    setShowVehicleJobInfo(false);
  };

  const jobInfoShowHandler = (jobId, vehicleNo, model, manufacturer) => {
    setJob(jobId);
    setVehNo(vehicleNo);
    setVehModel(model);
    setVehManufacturer(manufacturer);

    setShowVehicleJobInfo((prevState) => !prevState);
  };

  const wsConnection = useMemo(
    () => new WebSocketConnection(locationSocketUrl, {}),
    [],
  );

  const onMessage = useCallback((data) => {
    let id = 1;
    for (let [terminalId, locationData] of Object.entries(data)) {
      const pathColor = pathColors[id++];

      setVehicleLiveLocations((prevState) => ({
        ...prevState,
        [terminalId]: locationData,
      }));
      setVehiclePathData((prevState) => ({
        ...prevState,
        [terminalId]: prevState[terminalId]
          ? {
              ...prevState[terminalId],
              path: prevState[terminalId].path
                ? [
                    ...prevState[terminalId].path,
                    [locationData.latitude, locationData.longitude],
                  ]
                : [[locationData.latitude, locationData.longitude]],
            }
          : {
              color: { color: pathColor },
              path: [[locationData.latitude, locationData.longitude]],
            },
      }));
    }
  }, []);

  const payload = useMemo(
    () => ({
      username: user.username,
      terminalIds: results,
    }),
    [results, user.username],
  );

  const headers = useMemo(() => ({ "content-type": "application/json" }), []);

  const beforeSend = useCallback(() => setVehicleLiveLocations({}), []);

  const { wsStatus, loadingMessage } = useLiveLocation({
    wsConnection,
    messageMapping: {
      endpoint: `/locationservice/mission-control/live-location/${user.username}`,
      headers,
      payload,
    },
    topic: `/topic/mission-control/liveLocation/${user.username}`,
    onMessage,
    beforeSend,
  });

  return (
    <>
      {wsStatus !== statusConst.SUCCESS ? (
        <LoadingSpinner
          spinner={wsStatus === statusConst.FAILURE ? "error" : "gps-loader"}
          message={loadingMessage}
        />
      ) : (
        <div className="mission-control-map-container">
          <SideNav
            sideNavHandler={(value) => {
              setOpenSideNav(value);
            }}
            isSideNavOpen={openSideNav}
            items={{
              "search-filtration": {
                Icon: Sliders,
                content: (
                  <SearchFiltration
                    eventOptions={eventOptions}
                    jobStatusOptions={jobStatusOptions}
                    isLoading={isLoading}
                  />
                ),
              },
              "map-settings": {
                Icon: Settings,
                content: <MapSettings isLoading={false} />,
              },
            }}
          />
          <FloatingSearch
            style={{
              left:
                showVehicleJobInfo && openSideNav
                  ? "500px"
                  : showVehicleJobInfo
                    ? "700px"
                    : openSideNav
                      ? "500px"
                      : "80px",
            }}
          />
          {showVehicleJobInfo && (
            <MoreInfo
              style={{
                top: openSideNav ? "70px" : "20px",
                left: openSideNav ? "500px" : "80px",
              }}
              isSideNavOpen={openSideNav}
              job={job}
              vehNo={vehNo}
              model={vehModel}
              manufacturer={vehManufacturer}
              closeJobInfo={closeJobInfo}
            />
          )}
          <MapContainer
            className="mission-control-map"
            center={[7.147793, 79.946156]}
            zoom={8}
            zoomControl={false}
          >
            <TileLayer
              attribution="Google Maps"
              url="https://www.google.cn/maps/vt?lyrs=m@189&gl=cn&x={x}&y={y}&z={z}"
            />
            <MarkerClusterGroup chunkedLoading>
              {Object.entries(vehicleLiveLocations).map(
                ([terminalId, data]) => (
                  <MovingMarker
                    terminalId={terminalId}
                    rotation={data.direction}
                    lat={data.latitude}
                    lng={data.longitude}
                    speed={data.speed}
                    vehicleNo={data.vehicleNo}
                  >
                    <VehiclePopup
                      key={data.terminalIds}
                      socketData={data}
                      jobVehicleInfoToggle={jobInfoShowHandler}
                      ds={routeService.GRASSHOPPER}
                    />
                  </MovingMarker>
                ),
              )}
            </MarkerClusterGroup>
            {Object.entries(showPath).map(([terminalId, show]) => {
              if (show) {
                return (
                  <Polyline
                    pathOptions={vehiclePathData[terminalId].color}
                    positions={vehiclePathData[terminalId].path}
                    key={terminalId}
                  />
                );
              } else {
                return <></>;
              }
            })}
          </MapContainer>
        </div>
      )}
    </>
  );
};

export default MissionControlMapOS;
